import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Layout, 
  BarChart, 
  FileText, 
  Settings, 
  MapPin, 
  TrendingUp, 
  Star, 
  Clock, 
  Shield, 
  Wrench, 
  ClipboardCheck,
  ChevronDown,
  Calendar,
  Search,
  Download,
  Filter,
  ArrowUp,
  ArrowDown,
  RefreshCw,
  ChevronRight,
  DollarSign
} from 'lucide-react';
import { motion } from 'framer-motion';

// Import components
import Overview from './components/overview/Overview';
import Analytics from './components/analytics/Analytics';
import RegionMap from './components/map/RegionMap';
import Reports from './components/Reports/Reports';
import ScorecardSettings from './components/settings/ScorecardSettings';
import Sales from './components/sales/Sales';

// Import mock data
import { 
  getOverviewData, 
  getStoresData, 
  getRegionsData, 
  getHistoricalData 
} from './data/mockData';

const ScorecardDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [periodFilter, setPeriodFilter] = useState('month');
  const [regionFilter, setRegionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [storesData, setStoresData] = useState([]);

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      // Simulate API calls
      const overview = await getOverviewData(periodFilter, regionFilter);
      const stores = await getStoresData(periodFilter, regionFilter);
      
      setOverviewData(overview);
      setStoresData(stores);
      setIsLoading(false);
    };
    
    loadData();
  }, [periodFilter, regionFilter]);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Layout },
    { id: 'analytics', label: 'Analytics', icon: BarChart },
    { id: 'sales', label: 'Sales', icon: DollarSign },
    { id: 'map', label: 'Region Map', icon: MapPin },
    { id: 'reports', label: 'Reports', icon: FileText },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  // Helper function to get score color
  const getScoreColor = (score) => {
    if (score >= 85) return 'text-emerald-600';
    if (score >= 70) return 'text-amber-600';
    return 'text-rose-600';
  };

  // Helper function to get background color for score
  const getScoreBgColor = (score) => {
    if (score >= 85) return 'bg-emerald-100';
    if (score >= 70) return 'bg-amber-100';
    return 'bg-rose-100';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-700 to-indigo-800 rounded-xl p-6 text-white">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-semibold">Store Scorecard</h1>
            <p className="text-blue-100 mt-1">Comprehensive performance analytics for all stores</p>
          </div>
          <button
            onClick={() => setActiveTab('reports')}
            className="px-4 py-2 bg-white text-blue-700 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2 font-medium"
          >
            <Download className="w-5 h-5" />
            Export Report
          </button>
        </div>

        {/* Centered Tabs */}
        <div className="mt-6 flex justify-center">
          <div className="inline-flex space-x-1 bg-white/10 rounded-lg p-1 backdrop-blur-sm">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activeTab === tab.id
                      ? 'bg-white text-blue-700'
                      : 'text-white/90 hover:bg-white/10'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200">
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Time Period Filter */}
            <div className="relative inline-block text-left">
              <div>
                <button 
                  type="button" 
                  className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  id="period-menu-button"
                >
                  <Calendar className="mr-2 h-5 w-5 text-gray-400" />
                  {periodFilter === 'day' && 'Today'}
                  {periodFilter === 'week' && 'This Week'}
                  {periodFilter === 'month' && 'This Month'}
                  {periodFilter === 'quarter' && 'This Quarter'}
                  {periodFilter === 'year' && 'This Year'}
                  <ChevronDown className="ml-2 -mr-1 h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Region Filter */}
            <div className="relative inline-block text-left">
              <div>
                <button 
                  type="button" 
                  className="inline-flex justify-center w-full rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  id="region-menu-button"
                >
                  <MapPin className="mr-2 h-5 w-5 text-gray-400" />
                  {regionFilter === 'all' ? 'All Regions' : regionFilter}
                  <ChevronDown className="ml-2 -mr-1 h-5 w-5 text-gray-400" />
                </button>
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
              placeholder="Search stores..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <div className="absolute inset-y-0 right-0 flex py-1.5 pr-1.5">
              <kbd className="inline-flex items-center px-2 font-sans text-sm font-medium text-gray-400">
                ⌘K
              </kbd>
            </div>
          </div>
        </div>
      </div>

      {/* Loading state */}
      {isLoading && (
        <div className="flex justify-center items-center p-12">
          <RefreshCw className="w-8 h-8 text-blue-500 animate-spin" />
          <span className="ml-2 text-gray-600">Loading scorecard data...</span>
        </div>
      )}

      {/* Tab Content */}
      {!isLoading && (
        <div className="space-y-6">
          {activeTab === 'overview' && <Overview data={overviewData} stores={storesData} period={periodFilter} region={regionFilter} setRegionFilter={setRegionFilter} setPeriodFilter={setPeriodFilter} />}
          {activeTab === 'analytics' && <Analytics period={periodFilter} region={regionFilter} setRegionFilter={setRegionFilter} setPeriodFilter={setPeriodFilter} />}
          {activeTab === 'sales' && <Sales period={periodFilter} region={regionFilter} setRegionFilter={setRegionFilter} setPeriodFilter={setPeriodFilter} />}
          {activeTab === 'map' && <RegionMap />}
          {activeTab === 'reports' && <Reports />}
          {activeTab === 'settings' && <ScorecardSettings />}
        </div>
      )}

      {/* Summary Statistics */}
      {!isLoading && activeTab === 'overview' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Performance KPIs */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Top Performing Stores</h2>
              <p className="text-sm text-gray-500 mt-1">Based on overall scorecard ranking</p>
            </div>
            <div className="divide-y divide-gray-100">
              {storesData.slice(0, 5).map((store, index) => (
                <Link 
                  key={store.id} 
                  to={`/scorecard/store/${store.id}`}
                  className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center font-semibold text-blue-600">
                    {index + 1}
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{store.name}</p>
                      <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${getScoreBgColor(store.totalScore)} ${getScoreColor(store.totalScore)}`}>
                        {store.totalScore}/100
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">{store.region} - {store.city}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Link>
              ))}
              <div className="p-4">
                <Link 
                  to="/scorecard/analytics"
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                >
                  View all rankings
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* Underperforming Stores */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Stores Needing Attention</h2>
              <p className="text-sm text-gray-500 mt-1">Lowest scoring stores</p>
            </div>
            <div className="divide-y divide-gray-100">
              {[...storesData]
                .sort((a, b) => a.totalScore - b.totalScore)
                .slice(0, 5)
                .map((store, index) => (
                <Link 
                  key={store.id} 
                  to={`/scorecard/store/${store.id}`}
                  className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-shrink-0 h-10 w-10 rounded-full bg-rose-100 flex items-center justify-center font-semibold text-rose-600">
                    {index + 1}
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{store.name}</p>
                      <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${getScoreBgColor(store.totalScore)} ${getScoreColor(store.totalScore)}`}>
                        {store.totalScore}/100
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">{store.region} - {store.city}</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </Link>
              ))}
              <div className="p-4">
                <Link 
                  to="/scorecard/analytics?filter=low"
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center"
                >
                  View all underperforming stores
                  <ChevronRight className="ml-1 h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>

          {/* Category Leaders */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">Category Leaders</h2>
              <p className="text-sm text-gray-500 mt-1">Best performing by category</p>
            </div>
            <div className="divide-y divide-gray-100">
              {[
                { category: 'Sales Performance', icon: TrendingUp, color: 'bg-blue-100 text-blue-600', store: 'Riyadh Central', score: 29 },
                { category: 'Google Reviews', icon: Star, color: 'bg-amber-100 text-amber-600', store: 'Jeddah Beach', score: 19.5 },
                { category: 'Maintenance', icon: Wrench, color: 'bg-rose-100 text-rose-600', store: 'Dammam Mall', score: 10 },
                { category: 'Audit Score', icon: ClipboardCheck, color: 'bg-purple-100 text-purple-600', store: 'Makkah City', score: 9.5 },
                { category: 'Speed of Service', icon: Clock, color: 'bg-emerald-100 text-emerald-600', store: 'Abha Heights', score: 14.8 },
                { category: 'Quality Control', icon: Shield, color: 'bg-cyan-100 text-cyan-600', store: 'Tabuk Central', score: 14.2 }
              ].map((item, index) => (
                <div 
                  key={index}
                  className="flex items-center p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className={`flex-shrink-0 h-10 w-10 rounded-full ${item.color.split(' ')[0]} flex items-center justify-center`}>
                    <item.icon className={`h-5 w-5 ${item.color.split(' ')[1]}`} />
                  </div>
                  <div className="ml-4 flex-1">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900">{item.category}</p>
                      <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${item.color}`}>
                        {item.score}/{item.category === 'Sales Performance' ? 30 : 
                          item.category === 'Google Reviews' ? 20 :
                          item.category === 'Speed of Service' || item.category === 'Quality Control' ? 15 : 10}
                      </div>
                    </div>
                    <p className="text-xs text-gray-500">Leader: {item.store}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScorecardDashboard; 